import apiClient from './client';

export interface Agent {
    id: string;
    code: string;
    userId: string;
    clientId: string;
    type: 'Agent' | 'SuperAgent' | 'Marchant';
    statutApi: 'agent' | 'superuser';
    actif: boolean;
    solde: number;
    commission: number;
    commissionUsd: number;
    qrCodeUrl?: string;
    createdAt: string;
    updatedAt: string;
    client?: {
        id: string;
        nom: string;
        prenom: string;
        email: string;
        telephone: string;
        ville?: string;
    };
    // For UI display convenience
    name?: string;
    email?: string;
    pinSetupUrl?: string; // PIN setup URL for QR code generation
}

export interface AgentConfig {
    agentId: string;
    canWithdrawDirect: boolean;
    requireAdminApproval: boolean;
    clientAccessMode: 'full' | 'assigned_only';
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateAgentRequest {
    nom: string;
    prenom: string;
    surnom?: string;
    email: string;
    telephone: string;
    sexe?: string;
    dateNaissance?: string;
    adresse?: string;
    ville?: string;
    type: 'Agent' | 'SuperAgent' | 'Marchant';
    devise?: string; // e.g. 'HTG', 'USD'
    actif?: boolean;
    adminId?: string; // ID of the admin creating this agent
}

export const agentsApi = {
    /**
     * Get all agents
     */
    async getAll(): Promise<{ agents: Agent[] }> {
        const response = await apiClient.get('/api/agents');
        return response.data;
    },

    /**
     * Get agent by ID
     */
    async getById(id: string): Promise<{ agent: Agent }> {
        const response = await apiClient.get(`/api/agents/${id}`);
        return response.data;
    },

    /**
     * Create a new agent (Admin flow)
     */
    async create(data: CreateAgentRequest): Promise<{ success: boolean; agent: Agent; pinSetupUrl?: string }> {
        const response = await apiClient.post('/api/agents/admin/create', data);
        return response.data;
    },

    /**
     * Activate an agent
     */
    async activate(id: string): Promise<{ success: boolean; message: string; agent: Agent }> {
        const response = await apiClient.post(`/api/agents/${id}/activate`);
        return response.data;
    },

    /**
     * Deactivate an agent
     */
    async deactivate(id: string): Promise<{ success: boolean; message: string; agent: Agent }> {
        const response = await apiClient.post(`/api/agents/${id}/deactivate`);
        return response.data;
    },

    /**
     * Update agent details
     */
    async update(id: string, data: Partial<CreateAgentRequest>): Promise<{ success: boolean; agent: Agent }> {
        const response = await apiClient.put(`/api/agents/${id}`, data);
        return response.data;
    },

    /**
     * Resend PIN Setup Link
     */
    async resendPinSetup(userId: string): Promise<{ success: boolean; message: string }> {
        const response = await apiClient.post('/api/auth/users/resend-pin-setup', {
            userId,
            frontendUrl: window.location.origin,
        });
        return response.data;
    },

    /**
     * Get agent config (can_withdraw_direct, require_admin_approval)
     */
    async getConfig(agentId: string): Promise<AgentConfig> {
        const response = await apiClient.get(`/api/agents/${agentId}/config`);
        return response.data.config;
    },

    /**
     * Update agent config (Admin only)
     */
    async updateConfig(agentId: string, canWithdrawDirect: boolean, clientAccessMode?: 'full' | 'assigned_only'): Promise<AgentConfig> {
        const body: Record<string, unknown> = { can_withdraw_direct: canWithdrawDirect };
        if (clientAccessMode !== undefined) body.client_access_mode = clientAccessMode;
        const response = await apiClient.put(`/api/agents/${agentId}/config`, body);
        return response.data.config;
    },

    /**
     * Get clients assigned to an agent (paginated)
     */
    async getClients(agentId: string, params?: { page?: number; search?: string }): Promise<{
        comptes: Array<{
            compte_id: string;
            compte_code: string;
            type_compte: string;
            solde_bs: number;
            solde_s: number;
            statut: string;
            agentId: string;
            client: {
                id: string;
                nom: string;
                prenom: string;
                telephone: string;
                email: string;
                surnom?: string;
            } | null;
            date_assignation: string | null;
        }>;
        total: number;
        success: boolean;
    }> {
        const response = await apiClient.get(`/api/agents/${agentId}/clients`, { params });
        return response.data;
    },
};
